import type { Issue } from '../../../../api/DigiobcanApi';
import { __ } from '../../../../utils';

/**
 * Skladanie akcie v pätičke detailu nálezu.
 *
 * Toto je jadro obrazovky: jedno tlačidlo, ktorého popis aj dôsledky sa skladajú
 * z prepínačov. Preto je to **čistá funkcia** bez JSX a bez hookov - dá sa
 * prečítať celá naraz a overiť v konzole. Pochádza z prototypu, ktorý
 * už v repozitári nie je (viď CLAUDE.md). Keby to bolo rozsypané po
 * komponente, každá zmena pravidiel by znamenala prekliknúť osem stavov v UI,
 * aby sa zistilo, či niečo nevypadlo.
 *
 * Vety vysvetľujúceho textu sú zámerne pole a nie jeden reťazec: ich poradie je
 * súčasťou zadania a v poli je vidieť, ktorá veta kedy pribudne.
 */

/** Smer duplicity, ktorý úradník zvolil. Východzí je `child`. */
export type DuplicateDirection = 'child' | 'parent' | 'none';

/** Prepínače a výbery, ktoré drží detail nálezu. */
export type IssueDecisionState = {
  accept: boolean;
  resolve: boolean;
  recategorize: boolean;
  /** Vybrané nové kategórie pri prekategorizovaní. */
  categoryIds: string[];
  duplicateDirection: DuplicateDirection;
  /** Vybraní kandidáti na duplicitu. Pri `child` najviac jeden. */
  duplicatePicked: string[];
  statementText: string;
};

export type IssueDecisionInput = {
  status: Issue['status'];
  categoryName: string;
  /**
   * Podnet, ktorému nález patrí, je archivovaný.
   *
   * Je to príznak **podnetu**, nie nálezu, a chodí sem preto, že rozhodnutie
   * o náleze je jediné miesto, kde by sa dal archivovaný podnet ešte pohnúť.
   * API taký zápis odmieta (`409 REPORT_ARCHIVED`), takže bez tohto by
   * tlačidlo sľubovalo niečo, čo skončí chybou - to isté pravidlo ako pri
   * `movedCategoryIds`.
   */
  archived?: boolean;
  /**
   * Nález leží v kategórii, ktorú prihlásený úradník **len vidí**.
   *
   * Od septembra 2026 sa rozsah čítania a rozsah zápisu rozchádzajú:
   * `pruser:report-categories:read-all` (sekretariáty) dá celý zoznam podnetov
   * na čítanie, ale zapisovať sa smie naďalej len vo vlastných kategóriách.
   * API taký zápis odmieta `403 FORBIDDEN_ISSUE_STATE_UPDATE`, takže bez tohto
   * by tlačidlo sľubovalo niečo, čo skončí chybou - to isté pravidlo ako pri
   * `archived` a `movedCategoryIds`.
   */
  unmanaged?: boolean;
  /** Nález už je duplikátom iného - potom sa o duplicitách nerozhoduje. */
  parentId?: string;
  hasPossibleDuplicates: boolean;
  /**
   * Nález leží v kategórii, do ktorej podnety padajú, kým ich niekto nezaradí
   * (príznak `is_default` v API). Nie je to kategória, v ktorej sa
   * niečo rieši - je to čakáreň, takže sa v nej nedá prijímať a rovno sa
   * ponúka prekategorizovanie.
   *
   * Chodí sem ako príznak a nie ako `categoryId`, aby súbor zostal čistou
   * funkciou bez väzby na konfiguráciu - porovnanie robí volajúci.
   */
  isDefaultCategory?: boolean;
  /**
   * Kategórie, z ktorých už tento nález raz presunuli. Prekategorizovať do nich
   * sa nedá - vznikla by slučka, v ktorej si dve kategórie podnet prehadzujú
   * dokola. API to odmieta (`EXISTING_ISSUES_IN_CATEGORIES`), takže sa to musí
   * vedieť aj tu, aby tlačidlo nesľubovalo niečo, čo skončí chybou.
   */
  movedCategoryIds?: string[];
  /**
   * Vyjadrenia prihláseného úradníka v tejto kategórii **čakajú na
   * schválenie** (`requires_approval` na jeho členstve). Tlačidlo potom nič
   * nezverejňuje ani nerieši - posiela na schválenie: nález zostáva V riešení
   * a vyrieši sa až schválením finálneho vyjadrenia.
   */
  requiresApproval?: boolean;
  /**
   * Na náleze je vyjadrenie v schvaľovaní (`statement_in_review_id`): čaká na
   * schvaľovateľa (`pending`), alebo ho schvaľovateľ vrátil autorovi na úpravu
   * (`rejected`). Kým tam je, ďalšie vyjadrenie ani vyriešenie API nepustí
   * (`409 STATEMENT_PENDING_APPROVAL`) - a to **nikomu**, nielen autorovi.
   * Duplicita ostáva.
   */
  statementInReview?: 'pending' | 'rejected';
};

export type IssueDecisionPayload = {
  accept?: boolean;
  resolve?: boolean;
  new_category_ids?: string[];
  duplicate_of?: string;
  duplicates?: string[];
  dismiss_duplicates?: boolean;
  statement?: { text: string };
};

export type IssueDecision = {
  /** Popis tlačidla poskladaný z častí, spájaných ` + `. */
  label: string;
  disabled: boolean;
  /** Vety nad tlačidlom, v poradí. */
  sentences: string[];
  /** Telo pre `adminIssueDecision`. `undefined`, keď sa nedá odoslať. */
  payload?: IssueDecisionPayload;

  // Viditeľnosť sekcií - tabuľka z handoffu, na jednom mieste.
  showAccept: boolean;
  showRecategorize: boolean;
  showDuplicates: boolean;
  showStatement: boolean;
  showResolve: boolean;
  showFooter: boolean;
  readOnly: boolean;
  acceptLabel: string;
  /** Prepínač prijatia sa zobrazí, ale nedá sa zapnúť (čakáreň). */
  acceptDisabled: boolean;
  acceptHint: string;
  statementHint: string;
};

export const initialDecisionState = (
  issue: IssueDecisionInput,
): IssueDecisionState => ({
  // Nález, ktorý čaká na úradníka, sa najčastejšie prijíma - prepínač je preto
  // zapnutý a úradník ho vypína, len keď nález u seba nechce. V čakárni je to
  // naopak: prijať sa tam nedá vôbec a jediné, čo s nálezom ide urobiť, je
  // poslať ho tomu, komu patrí - preto je rovno zapnuté prekategorizovanie.
  accept: issue.status === 'open' && !issue.isDefaultCategory,
  resolve: false,
  recategorize: !!issue.isDefaultCategory && issue.status === 'open',
  categoryIds: [],
  // Smer sa ponúka len tomu, kto ešte duplikátom nie je - o zlúčenom náleze
  // sa už nerozhoduje. Kandidát duplikátom byť pokojne môže: API vzťah skráti
  // na jeho originál.
  duplicateDirection: issue.parentId ? 'none' : 'child',
  duplicatePicked: [],
  statementText: '',
});

export const buildDecision = (
  issue: IssueDecisionInput,
  state: IssueDecisionState,
): IssueDecision => {
  const inProgress = issue.status === 'accepted';
  const done = issue.status === 'resolved';
  const isDuplicate = !!issue.parentId;

  // Archivovaný podnet sa nerieši - najprv sa obnoví. Stojí to **pred**
  // ostatnými vetvami, lebo archivácia prebíja stav nálezu: archivovaný podnet
  // s nepridelným nálezom sa nedá prijať rovnako ako ten s prijatým.
  //
  // Dôvod sa tu nevypisuje do `sentences`: `showFooter: false` ich nevykreslí
  // a je to fakt o celom podnete, takže patrí do pásu nad obálkou, nie na
  // každú kartu nálezu zvlášť.
  if (issue.archived) {
    return {
      label: '',
      disabled: true,
      sentences: [],
      showAccept: false,
      showRecategorize: false,
      showDuplicates: false,
      showStatement: false,
      showResolve: false,
      showFooter: false,
      readOnly: true,
      acceptLabel: '',
      acceptDisabled: false,
      acceptHint: '',
      statementHint: '',
    };
  }

  // Cudzia kategória: úradník ju vidí, ale nerieši. Stojí to hneď za
  // archiváciou a z rovnakého dôvodu - je to fakt o prístupe, ktorý prebíja
  // stav nálezu, takže sa nesmie posudzovať až vnútri vetiev nižšie.
  if (issue.unmanaged) {
    return {
      label: '',
      disabled: true,
      sentences: [],
      showAccept: false,
      showRecategorize: false,
      showDuplicates: false,
      showStatement: false,
      showResolve: false,
      showFooter: false,
      readOnly: true,
      acceptLabel: '',
      acceptDisabled: false,
      acceptHint: '',
      statementHint: '',
    };
  }

  // Duplikát nemá čo rozhodovať. Rieši sa v origináli, ktorého riešenie aj
  // vyjadrenie preberá, takže prijatie, prekategorizovanie ani vlastné
  // stanovisko nedávajú zmysel - a prekategorizovanie by nález navyše
  // vytrhlo z kategórie, v ktorej ho drží dvojica s originálom. Na obrazovke
  // zostáva len informácia, koho duplikátom je, a odkaz na originál.
  // Presunutý nález je uzavretá história: úradník ho poslal do inej kategórie
  // a u seba ho už nemá. Rozhodovať sa o ňom nedá - jediná cesta späť je
  // „znovu prijať", ktorá stojí na `adminUpdateReportStatus` a nie na
  // rozhodnutí. Na obrazovke zostáva informácia o presune a to tlačidlo.
  if (issue.status === 'moved') {
    return {
      label: '',
      disabled: true,
      sentences: [],
      showAccept: false,
      showRecategorize: false,
      showDuplicates: false,
      showStatement: false,
      showResolve: false,
      showFooter: false,
      readOnly: true,
      acceptLabel: '',
      acceptDisabled: false,
      acceptHint: '',
      statementHint: '',
    };
  }

  if (isDuplicate) {
    return {
      label: '',
      disabled: true,
      sentences: [],
      showAccept: false,
      showRecategorize: false,
      showDuplicates: false,
      showStatement: false,
      showResolve: false,
      showFooter: false,
      readOnly: true,
      acceptLabel: '',
      acceptDisabled: false,
      acceptHint: '',
      statementHint: '',
    };
  }

  /**
   * Prijatie v čakárni je zamknuté, nie len vypnuté.
   *
   * `state.accept` sa preto ďalej nečíta priamo: keby v ňom ostalo `true`
   * z nálezu, ktorý bol na obrazovke predtým, poskladalo by tlačidlo akciu,
   * ktorú prepínač neponúka. Rozhodnutie má hovoriť to isté, čo obrazovka.
   */
  const acceptDisabled = !!issue.isDefaultCategory;
  const accept = state.accept && !acceptDisabled;

  const categoryCount = state.categoryIds.length;
  const picked = state.duplicatePicked.length;
  // Vybrané kategórie, do ktorých sa nález vrátiť nesmie.
  const loopCategoryIds = state.categoryIds.filter((id) =>
    (issue.movedCategoryIds ?? []).includes(id),
  );

  // Prekategorizovať sa dá len nález, o ktorom úrad ešte nerozhodol.
  const showRecategorize = !inProgress && !done;
  // O smere duplicity sa rozhoduje len vtedy, keď je z čoho vyberať. Prototyp
  // mal kandidátov ako konštantu, takže sekcia stála vždy; v appke ich väčšina
  // nálezov nemá žiadnych, a bez tejto podmienky by `duplicateMissing` nechal
  // tlačidlo natrvalo neaktívne - nedal by sa prijať ani úplne obyčajný nález.
  const duplicatesOffered =
    !done && !isDuplicate && issue.hasPossibleDuplicates;
  const duplicateMissing =
    duplicatesOffered && state.duplicateDirection !== 'none' && picked === 0;

  // Čakajúce vyjadrenie zamyká vyjadrenie aj vyriešenie - najprv rozhodne
  // schvaľovateľ.
  const statementLocked = !!issue.statementInReview;
  const approval = !!issue.requiresApproval;
  const resolvedNow =
    !done && (inProgress || accept) && state.resolve && !statementLocked;
  const statementText = statementLocked ? '' : state.statementText.trim();
  // Nález sa stáva duplikátom iného. Tento výsledok **prebíja** zmenu stavu:
  // preberá riešenie aj vyjadrenie originálu, takže vlastné stanovisko ani
  // prijatie nedávajú zmysel.
  const asChild =
    duplicatesOffered && state.duplicateDirection === 'child' && picked > 0;

  const parts: string[] = [];

  if (!asChild) {
    if (inProgress && !statementLocked) {
      parts.push(
        resolvedNow
          ? approval
            ? __('Navrhnúť vyriešenie')
            : __('Vyriešiť')
          : approval
            ? __('Odoslať na schválenie')
            : __('Uložiť vyjadrenie'),
      );
    } else if (!done && accept) {
      parts.push(
        resolvedNow
          ? approval
            ? __('Prijať a navrhnúť vyriešenie')
            : __('Prijať a vyriešiť')
          : __('Prijať'),
      );
    }
  }

  if (showRecategorize && state.recategorize) {
    parts.push(parts.length ? __('prekategorizovať') : __('Prekategorizovať'));
  }

  if (duplicatesOffered && picked > 0) {
    const first = state.duplicateDirection === 'child';
    parts.push(
      parts.length
        ? first
          ? __('označiť ako duplikát')
          : __('zlúčiť duplicity')
        : first
          ? __('Označiť ako duplikát')
          : __('Zlúčiť duplicity'),
    );
  }

  /**
   * Vyjadrenie je povinné **len** pri vyriešení.
   *
   * Priebežná poznámka k otvorenému nálezu môže chýbať - úradník ho môže
   * prijať a napísať sa neskôr. Vyriešením sa ale nález nenávratne uzatvára
   * a to, čo sa pri ňom občanovi zverejní ako finálne stanovisko úradu, je
   * práve tento text; prázdny by znamenal uzavretý podnet bez odpovede.
   * Pri duplikáte sa nepýta: ten preberá stanovisko originálu.
   */
  const statementMissing = resolvedNow && !asChild && !statementText;

  // Duplicity vedia niesť rozhodnutie samy: nielen vybraní kandidáti, ale aj
  // voľba „nie sú to duplikáty", ktorá návrhy zahodí (`dismiss_duplicates`).
  const duplicateDecided =
    duplicatesOffered && (picked > 0 || state.duplicateDirection === 'none');

  /**
   * Prijatý nález nemá čo uložiť, kým úradník niečo nenapíše.
   *
   * V stave V riešení už prepínač prijatia nie je a prekategorizovať sa nedá,
   * takže „Uložiť vyjadrenie" je jediná akcia - a bez textu by to bolo volanie
   * API, po ktorom sa nezmení nič. Kým táto podmienka chýbala, tlačidlo
   * svietilo aktívne hneď po prijatí nálezu, keď sa formulár vyprázdnil.
   * Prílohy samé nestačia: vyjadrenie bez textu nevznikne a nemali by kam
   * pribudnúť.
   */
  const nothingToSave =
    inProgress &&
    !statementLocked &&
    !resolvedNow &&
    !statementText &&
    !duplicateDecided;

  const label = parts.length ? parts.join(' + ') : __('Vyberte akciu');
  const disabled =
    !parts.length ||
    (showRecategorize && state.recategorize && categoryCount === 0) ||
    loopCategoryIds.length > 0 ||
    duplicateMissing ||
    statementMissing ||
    nothingToSave;

  const sentences: string[] = [];

  if (asChild) {
    sentences.push(
      `Nález sa uzavrie ako duplikát ${state.duplicatePicked[0]} a preberie jeho riešenie aj vyjadrenie - samostatné stanovisko sa nezverejní.`,
    );
  } else if (duplicatesOffered && picked > 0) {
    sentences.push(
      `${picked} ${picked === 1 ? 'nález sa zlúči' : 'nálezy sa zlúčia'} do tohto ako duplikát.`,
    );
  }

  if (issue.statementInReview === 'pending') {
    sentences.push(
      __(
        'Vyjadrenie k nálezu čaká na schválenie. Kým o ňom schvaľovateľ nerozhodne, ďalšie sa pridať ani nález vyriešiť nedá.',
      ),
    );
  } else if (issue.statementInReview === 'rejected') {
    sentences.push(
      __(
        'Vyjadrenie k nálezu bolo vrátené na úpravu. Kým ho autor nepošle znova alebo nestiahne, ďalšie sa pridať ani nález vyriešiť nedá.',
      ),
    );
  }

  if (!parts.length) {
    if (!statementLocked) {
      sentences.push(
        __('Zapnite akciu alebo prekategorizovanie - nič sa nezmení.'),
      );
    }
  } else if (showRecategorize && state.recategorize && categoryCount === 0) {
    sentences.push(__('Vyberte aspoň jednu novú kategóriu.'));
  }

  if (loopCategoryIds.length) {
    sentences.push(
      __(
        'Do kategórie, z ktorej už nález presunuli, ho priradiť nemožno - musí si ho prevziať kolega, ktorý ju má na starosti.',
      ),
    );
  }

  if (statementMissing) {
    sentences.push(
      approval
        ? __(
            'Napíšte vyjadrenie - po schválení sa zverejní ako finálne stanovisko úradu.',
          )
        : __(
            'Napíšte vyjadrenie - zverejní sa ako finálne stanovisko úradu k vyriešenému nálezu.',
          ),
    );
  }

  if (nothingToSave) {
    sentences.push(__('Napíšte vyjadrenie - bez textu sa nič neuloží.'));
  }

  if (duplicateMissing) {
    sentences.push(
      state.duplicateDirection === 'child'
        ? __('Vyberte nález, ktorý sa rieši ako originál.')
        : __('Vyberte nálezy, ktoré sa majú zlúčiť do tohto.'),
    );
  }

  // Pri `asChild` sa vety o prijatí a vyriešení nepíšu - výsledok duplikátu je
  // nadradený zmene stavu.
  if (!asChild) {
    if (resolvedNow && approval) {
      sentences.push(
        __(
          'Vyjadrenie pôjde na schválenie. Nález sa vyrieši až jeho schválením - dovtedy zostáva V riešení a lehota beží.',
        ),
      );
    } else if (resolvedNow) {
      sentences.push(
        __(
          'Nález prejde do stavu Vyriešené a uzavrie sa - späť sa otvoriť nedá. Vyjadrenie sa zverejní ako finálne stanovisko úradu.',
        ),
      );
    } else if (inProgress && !nothingToSave && !statementLocked) {
      sentences.push(
        approval
          ? __(
              'Vyjadrenie pôjde na schválenie a občanovi sa zverejní až po ňom. Nález zostáva V riešení.',
            )
          : __(
              'Nález zostáva v stave V riešení, vyjadrenie sa zverejní pri podnete ako priebežné stanovisko.',
            ),
      );
    } else if (accept) {
      sentences.push(
        approval && statementText
          ? `Nález prejde do stavu V riešení v kategórii ${issue.categoryName}. Vyjadrenie pôjde na schválenie.`
          : `Nález prejde do stavu V riešení v kategórii ${issue.categoryName}. Vyjadrenie sa zverejní, nález zostáva otvorený.`,
      );
    }
  }

  if (showRecategorize && state.recategorize && categoryCount > 0) {
    sentences.push(
      `Nález dostane ${categoryCount} ${
        categoryCount === 1 ? 'novú kategóriu' : 'nové kategórie'
      } a pôvodná ${issue.categoryName} ${
        accept ? 'v podnete ostáva.' : 'sa z podnetu odoberie.'
      }`,
    );
  }

  sentences.push(__('Ostatné nálezy podnetu to neovplyvnia.'));

  const showStatement =
    done || ((inProgress || accept) && !asChild && !statementLocked);
  const showResolve =
    !done && !asChild && (inProgress || accept) && !statementLocked;

  const payload: IssueDecisionPayload | undefined = disabled
    ? undefined
    : {
        ...(asChild
          ? { duplicate_of: state.duplicatePicked[0] }
          : {
              ...(!done && !inProgress && accept && { accept: true }),
              ...(resolvedNow && { resolve: true }),
              ...(statementText && {
                statement: { text: statementText },
              }),
            }),
        ...(showRecategorize &&
          state.recategorize &&
          categoryCount > 0 && { new_category_ids: state.categoryIds }),
        ...(duplicatesOffered &&
          state.duplicateDirection === 'parent' &&
          picked > 0 && { duplicates: state.duplicatePicked }),
        // „Nie sú to duplikáty" je aktívna voľba úradníka (východzí smer je
        // `child`), takže odoslanie s ňou naozaj znamená zahodiť návrhy.
        ...(duplicatesOffered &&
          state.duplicateDirection === 'none' && { dismiss_duplicates: true }),
      };

  return {
    label,
    disabled,
    sentences,
    payload,
    showAccept: showRecategorize,
    showRecategorize,
    showDuplicates: duplicatesOffered,
    showStatement,
    showResolve,
    // Čakajúce vyjadrenie bez inej možnej akcie: pätička by ponúkala neaktívne
    // „Vyberte akciu". Čo sa deje, hovorí blok o schvaľovaní v tele karty.
    showFooter: !done && !(statementLocked && !parts.length),
    readOnly: done,
    acceptLabel: `Prijať podnet v kategórii ${issue.categoryName}`,
    acceptDisabled,
    acceptHint: acceptDisabled
      ? __('V tejto kategórii sa nerieši - vyberte, komu podnet patrí')
      : accept
        ? __('Nález prejde k vám do riešenia')
        : __('Nález u vás nezostáva'),
    statementHint: done
      ? __('finálne stanovisko úradu · nález je uzavretý')
      : showResolve && state.resolve
        ? approval
          ? __(
              'povinné · finálne stanovisko - pred zverejnením ho schváli vedúci',
            )
          : __(
              'povinné · finálne stanovisko - nález sa ním nenávratne uzatvára',
            )
        : approval
          ? __(
              'nepovinné · priebežné stanovisko - pred zverejnením ho schváli vedúci',
            )
          : __('nepovinné · priebežné stanovisko k otvorenému nálezu'),
  };
};
